'use client'

import { cn } from '@/lib/tailwind'
import { useVirtualList } from '@/lib/use-virtual-list'
import type { FeedEntry } from './types'

/**
 * Virtual list component for efficiently rendering large feeds.
 *
 * Note: This virtualization wrapper does NOT interfere with click events.
 * The wrapper divs are purely for positioning and do not capture or block
 * any user interactions. All click handlers in rendered items work normally.
 */
interface VirtualFeedProps {
  entries: FeedEntry[]
  renderItem: (entry: FeedEntry, index: number) => React.ReactNode
  estimatedItemHeight?: number // Default: 48px for typical collapsed row
  className?: string
  emptyState?: React.ReactNode
}

export function VirtualFeed({
  entries,
  renderItem,
  estimatedItemHeight = 48,
  className,
  emptyState,
}: VirtualFeedProps) {
  const { containerRef, virtualItems, totalSize, measureElement } =
    useVirtualList({
      items: entries,
      estimateSize: estimatedItemHeight,
      getItemKey: (index, entry) => `${entry.type}-${entry.data.id}`,
    })

  if (entries.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <div
      ref={containerRef}
      className={cn('h-[calc(100vh-200px)] overflow-auto', className)}
    >
      <div
        style={{
          height: `${totalSize}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const entry = entries[virtualItem.index]
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {renderItem(entry, virtualItem.index)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
